import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../styles/pages.css";
import Banner from "../ui/Banner";
import products from "../../assets/images/products.png";
import Pagination from "../ui/Pagination";
import EnSearchBar from "../ui/EnSearchBar";
import { getListProduct, getCategories } from "../../api";

// 공통 ProductAreaContent 컴포넌트
const ProductAreaContent = ({ title, productList, category }) => {
  const navigate = useNavigate();
  // Pagination 시작
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const productsPerPage = 6;
  // Pagination 끝
  // 검색바
  const [originalProducts, setOriginalProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setOriginalProducts(productList);
    setFilteredProducts(productList);
  }, [productList]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredProducts(originalProducts);
    } else {
      const filtered = originalProducts.filter(
        (product) =>
          product.en_translations[0].title_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.en_translations[0].content_en.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredProducts(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, originalProducts]);

  useEffect(() => {
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    setDisplayedProducts(filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct));
  }, [currentPage, filteredProducts, productsPerPage]);

  const handleProductClick = (category, id, e) => {
    if (e) e.stopPropagation();
    navigate(`/en/products/${category}/${id}/`);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({
      top: document.querySelector(".productarea-header").offsetTop - 100,
      behavior: "smooth",
    });
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <div className="productarea-section">
      <div className="productarea-header">
        <h2>{title}</h2>
        <p>Introducing eco-farm's products, technology proven in the field.</p>
      </div>
      <div className="productarea-info">
        <p>{filteredProducts.length} items</p>
        <EnSearchBar onSearch={handleSearch} />
      </div>
      <hr />
      {displayedProducts.length > 0 ? (
        <div className="product-grid">
          {displayedProducts.map((product) => (
            <div
              key={product.id}
              className="product-box"
              onClick={(e) => handleProductClick(category, product.id, e)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleProductClick(category, product.id, e);
                }
              }}
            >
              <div className="product-thumnail-container">
                <img src={`${product.thumbnail.file.url}`} alt={product.name} className="product-thumnail" />
                <div className="hover-overlay">
                  <span className="detail-text">View Details</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="58" height="58" viewBox="0 0 58 58" className="arrownew-icon">
                    <g data-name="그룹 62">
                      <g data-name="그룹 61">
                        <path
                          data-name="패스 44"
                          d="M9118.341 1662.063h23.6"
                          transform="translate(-9101.257 -1633.028)"
                          style={{ strokeLinecap: "round", stroke: "#fff", strokeWidth: "2px", fill: "none" }}
                        />
                        <path
                          data-name="패스 45"
                          d="m9187.706 1640.815 5.992 5.993-5.992 5.992"
                          transform="translate(-9153.012 -1617.773)"
                          style={{ strokeLinejoin: "round", strokeLinecap: "round", stroke: "#fff", strokeWidth: "2px", fill: "none" }}
                        />
                      </g>
                      <g data-name="타원 1" style={{ stroke: "#fff", strokeWidth: "2px", fill: "none" }}>
                        <circle cx="29" cy="29" r="29" style={{ stroke: "none" }} />
                        <circle cx="29" cy="29" r="28" style={{ fill: "none" }} />
                      </g>
                    </g>
                  </svg>
                </div>
              </div>
              <div className="product-info">
                <h3>
                  {product.en_translations[0].title_en}
                  {product.subtitle && (
                    <>
                      <br />
                      {product.subtitle}
                    </>
                  )}
                </h3>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <p>There is no result.</p>
        </div>
      )}
      {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />}
    </div>
  );
};

const EnProducts = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [productData, setProductData] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [error, setError] = useState(null);
  // 언어 설정
  const currentLang = "en";

  // 카테고리 정보 가져오기
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const categoriesData = await getCategories();
        setCategories(categoriesData.data.results);
        setCategoryLoading(false);
      } catch (err) {
        setError("Failed to load category information.");
        setCategoryLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // 활성 탭 결정 (URL 파라미터 또는 첫 번째 카테고리)
  const activeTab = tab || (categories.length > 0 ? categories[0].en_name.toLowerCase() : "");

  // 탭이 변경될 때마다 해당 카테고리의 제품 데이터를 불러옴
  useEffect(() => {
    if (!activeTab) return;
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await getListProduct({ category: activeTab, lang: currentLang });
        setProductData((prev) => ({
          ...prev,
          [activeTab]: data.data,
        }));
        setLoading(false);
      } catch (err) {
        setError("Failed to load product information.");
        setLoading(false);
      }
    };

    // 이미 데이터가 있는지 확인
    if (!productData[activeTab]) {
      fetchProducts();
    } else {
      setLoading(false);
    }
  }, [activeTab, productData]);

  const handleTabChange = (tabId) => {
    navigate(`/en/products/${tabId}`);
  };

  // 카테고리 로딩 중이면 로딩 표시
  if (categoryLoading) {
    return <div className="loading">loading</div>;
  }

  // 카테고리 데이터로 탭 구성
  const bannerConfig = {
    backgroundImage: products,
    title: "product descriptions",
    description: ["The perfect solution for the farm world", "Introducing ECOTECH GLOBAL's products completed with proven technology."],
    tabs: [
      ...categories.map((category) => ({
        id: category.en_name.toLowerCase(),
        name: category.en_name,
      })),
      {
        id: "golf",
        name: "ratel park golf",
      },
    ],
  };

  // 카테고리 이름 매핑 생성
  const tabTitles = {};
  categories.forEach((category) => {
    tabTitles[category.en_name.toLowerCase()] = category.en_name;
  });

  const renderTabContent = () => {
    if (loading) return <div className="loading">loading</div>;
    if (error) return <div className="error">{error}</div>;

    const currentCategory = categories.find((cat) => cat.en_name.toLowerCase() === activeTab);

    if (!currentCategory) return <div className="error">There is no Category.</div>;

    return <ProductAreaContent title={currentCategory.en_name} productList={productData[activeTab] || []} category={activeTab} />;
  };

  return (
    <div className="product-container">
      <Banner {...bannerConfig} activeTab={activeTab} onTabChange={handleTabChange} />
      {renderTabContent()}
    </div>
  );
};

export default EnProducts;
